import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import { getInitials } from '../lib/utils';
import { NotificationBell } from './NotificationBell';
import ChangelogButton from './ChangelogButton';
import {
  LayoutDashboard, Users, CalendarDays, Receipt, Package, UserPlus, FolderKanban, Settings, LogOut, Menu, ChevronDown, Shield, Wallet, Calendar, Sparkles, Plane, ClipboardCheck, Eye, Clock, History, Bug, FileText, CreditCard, UserCheck, Upload, Briefcase, UserSearch, BarChart3, DollarSign, CloudDownload, Play, IndianRupee, PartyPopper, Target, ShoppingCart, Coins, PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_25355c40-a335-460f-bd4b-faf08ba5507b/artifacts/71a1dr43_logo%20without%20background.png";

// Navigation items with module IDs for dynamic RBAC
// module: corresponds to RBAC module ID from backend
const navigation = [
  // Core
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, module: 'dashboard' },
  { name: 'Guest Portal', href: '/guest-portal', icon: Eye, module: 'guest_portal' },
  
  // Employee Management
  { name: 'Employees', href: '/employees', icon: Users, module: 'employees' },
  { name: 'Employee Portal', href: '/employee-portal', icon: Wallet, module: null }, // Accessible to all employees
  { name: 'AD Sync', href: '/ad-sync', icon: CloudDownload, module: 'ad_sync' },
  
  // Attendance
  { name: 'Attendance', href: '/attendance', icon: UserCheck, module: 'attendance' },
  { name: 'Attendance Admin', href: '/attendance/admin', icon: ClipboardCheck, module: 'attendance_admin' },
  { name: 'Import Attendance', href: '/attendance/import', icon: Upload, module: 'attendance_admin' },
  
  // Recruitment
  { name: 'Requisitions', href: '/recruitment/requisitions', icon: Briefcase, module: 'recruitment' },
  { name: 'Candidates', href: '/recruitment/candidates', icon: UserSearch, module: 'recruitment' },
  { name: 'Offers', href: '/recruitment/offers', icon: DollarSign, module: 'recruitment_offers' },
  { name: 'Recruitment Analytics', href: '/recruitment/analytics', icon: BarChart3, module: 'recruitment' },
  
  // Timesheet
  { name: 'Timesheet', href: '/timesheet', icon: Clock, module: 'timesheets' },
  { name: 'Timesheet Approvals', href: '/timesheet/approvals', icon: ClipboardCheck, module: 'timesheet_approvals' },
  
  // Leave
  { name: 'Leave', href: '/leaves', icon: CalendarDays, module: 'leaves' },
  { name: 'Leave Encashment', href: '/leaves/encashment', icon: Wallet, module: 'leaves', roles: ['admin', 'super_admin', 'hr'] },
  
  // Celebrations (Birthday & Anniversary)
  { name: 'Celebrations', href: '/celebrations', icon: PartyPopper, module: 'celebrations' },
  
  // Performance Management
  { name: 'Performance', href: '/performance', icon: Target, module: 'performance' },
  { name: 'Performance & Discipline', href: '/discipline', icon: Shield, module: 'discipline', roles: ['admin', 'super_admin', 'hr'] },
  
  // Purchase Orders
  { name: 'Purchase Orders', href: '/purchase-orders', icon: ShoppingCart, module: 'purchase_orders' },
  
  // Project Costing
  { name: 'Project Costing', href: '/project-costing', icon: BarChart3, module: 'project_costing' },
  
  // Petty Cash
  { name: 'Petty Cash', href: '/petty-cash', icon: Coins, module: 'petty_cash' },
  
  // Expenses
  { name: 'Expenses', href: '/expenses', icon: Receipt, module: 'expenses' },
  
  // Travel
  { name: 'Travel', href: '/travel', icon: Plane, module: 'travel' },
  { name: 'Travel Approvals', href: '/travel/approvals', icon: ClipboardCheck, module: 'travel_approvals' },
  
  // Payroll
  { name: 'My Payroll', href: '/my-payroll', icon: Wallet, module: 'payroll' },
  { name: 'Payroll Runs', href: '/payroll/runs', icon: Play, module: 'payroll_runs' },
  { name: 'Payroll AI', href: '/payroll-ai', icon: Sparkles, module: 'payroll_runs' },
  
  // Billing
  { name: 'Invoices', href: '/billing/invoices', icon: FileText, module: 'billing' },
  { name: 'Payments', href: '/billing/payments', icon: CreditCard, module: 'billing' },
  
  // Accounts - GST & Taxes
  { name: 'GST & Taxes', href: '/accounts/gst', icon: IndianRupee, module: 'gst' },
  
  // Holidays
  { name: 'Holidays', href: '/holidays', icon: Calendar, module: 'holidays' },
  
  // Assets
  { name: 'Assets', href: '/assets', icon: Package, module: 'assets' },
  
  // CRM
  { name: 'CRM', href: '/crm', icon: UserPlus, module: ['crm', 'meta_connect'] },
  
  // DevOps
  { name: 'DevOps', href: '/devops', icon: FolderKanban, module: 'devops' },
  
  // Admin section
  { name: 'Error Logs', href: '/admin/errors', icon: Bug, module: 'error_logs' },
  { name: 'Audit Trail', href: '/admin/audit', icon: History, module: 'audit' },
  { name: 'Settings', href: '/settings', icon: Settings, module: 'settings' },
  { name: 'User Management', href: '/users', icon: Shield, module: 'users' },
  { name: 'Role Permissions', href: '/admin/rbac', icon: Shield, module: 'rbac' },
  { name: 'User Access', href: '/admin/user-access', icon: Users, module: 'user_access' },
];

const roleColors = {
  super_admin: 'bg-red-100 text-red-800',
  admin: 'bg-purple-100 text-purple-800',
  sales: 'bg-cyan-100 text-cyan-800',
  hr: 'bg-blue-100 text-blue-800',
  accounts: 'bg-green-100 text-green-800',
  employee: 'bg-indigo-100 text-indigo-800',
  guest: 'bg-gray-100 text-gray-800',
};

const roleLabels = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  hr: 'HR',
  accounts: 'Accounts',
  sales: 'Sales',
  employee: 'Employee',
  guest: 'Guest',
};

export function Layout({ children }) {
  const { user, logout, accessibleModules, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Extract module IDs from accessibleModules (handles both object and string formats)
  const moduleIds = Array.isArray(accessibleModules) 
    ? accessibleModules.map(m => typeof m === 'object' ? m.id : m)
    : [];

  // Filter navigation based on accessible modules
  const filteredNav = navigation.filter((item) => {
    // Super admin sees everything
    if (isSuperAdmin) return true;
    
    // If module is specified, check if user has access (array = ANY of them)
    if (item.module) {
      const mods = Array.isArray(item.module) ? item.module : [item.module];
      return mods.some((m) => moduleIds.includes(m));
    }
    
    // If no module specified, show by default
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50/30 to-white">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed left-0 top-0 h-full ${collapsed ? 'w-16' : 'w-64'} bg-white/95 backdrop-blur-xl border-r border-border z-50 transform transition-all duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="h-16 flex items-center px-4 border-b border-border justify-between">
            {!collapsed && (
              <Link to="/" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="Vibe6" className="h-10" />
              </Link>
            )}
            <Button variant="ghost" size="icon" className="hidden md:flex h-8 w-8 shrink-0" onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-collapse-btn">
              {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </Button>
          </div>

          <ScrollArea className="flex-1 py-4">
            <TooltipProvider delayDuration={0}>
              <nav className={`${collapsed ? 'px-2' : 'px-3'} space-y-1`}>
                {filteredNav.map((item) => {
                  const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
                  if (collapsed) {
                    return (
                      <Tooltip key={item.name}>
                        <TooltipTrigger asChild>
                          <Link
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`flex items-center justify-center p-2.5 rounded-lg transition-all ${isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
                          >
                            <item.icon className="w-5 h-5" />
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="font-medium">{item.name}</TooltipContent>
                      </Tooltip>
                    );
                  }
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </TooltipProvider>
          </ScrollArea>

          <div className="p-4 border-t border-border">
            {collapsed ? (
              <div className="flex justify-center">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-primary/10 text-primary">{getInitials(user?.name)}</AvatarFallback>
                </Avatar>
              </div>
            ) : (
              <div className="flex items-center gap-3 px-2">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-primary/10 text-primary">{getInitials(user?.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <Badge className={`text-xs ${roleColors[user?.role] || 'bg-gray-100'}`}>
                    {roleLabels[user?.role] || user?.role}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      <div className={`${collapsed ? 'md:pl-16' : 'md:pl-64'} transition-all duration-200`}>
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-lg border-b border-border">
          <div className="flex items-center justify-between h-full px-4 md:px-6">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex-1" />
            <div className="flex items-center gap-1">
              <ChangelogButton />
              <NotificationBell />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">{getInitials(user?.name)}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="font-medium">{user?.email}</DropdownMenuItem>
                <DropdownMenuItem>
                  <Badge className={`${roleColors[user?.role] || 'bg-gray-100'}`}>{roleLabels[user?.role] || user?.role}</Badge>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-4 md:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
